import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RankBadge } from "@/components/RankBadge";
import { Swords, Plus, X, Send, Timer, Users, Check, ShieldCheck, MessageCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const formatDuration = (since) => {
  const diff = Math.max(0, Date.now() - new Date(since).getTime());
  const m = Math.floor(diff / 60000).toString().padStart(2, "0");
  const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const STATUS_LABEL = {
  searching: { label: "Đang tìm", color: "text-amber-300", bg: "bg-amber-400/10 border-amber-400/30" },
  negotiating: { label: "Đang thương lượng", color: "text-cyan-300", bg: "bg-cyan-400/10 border-cyan-400/30" },
  matched: { label: "Đã ghép trận", color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
  cancelled: { label: "Đã hủy", color: "text-white/40", bg: "bg-white/[0.03] border-white/10" },
  expired: { label: "Hết hạn", color: "text-white/40", bg: "bg-white/[0.03] border-white/10" },
};

export default function Arena() {
  const { id } = useParams();
  if (id) return <RequestRoom rid={id} />;
  return <Lobby />;
}

const Lobby = () => {
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [tick, setTick] = useState(0);
  const load = () => api.get("/arena/requests").then(({ data }) => setList(data));

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (["arena.new", "arena.interest", "arena.matched", "arena.cancelled"].includes(ev.type)) load();
        } catch {}
      };
    } catch {}
    return () => { clearInterval(i); clearInterval(t); if (ws) ws.close(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Swords className="text-amber-300" />
            <h1 className="font-display font-bold text-3xl sm:text-4xl">Đấu trường</h1>
          </div>
          <button onClick={() => setCreating(true)} data-testid="create-arena-btn" className="px-4 py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black flex items-center gap-2">
            <Plus size={15} /> Tạo kèo
          </button>
        </div>

        {list.length === 0 ? (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-12 text-center text-white/40">
            Chưa có kèo nào. Tạo kèo đầu tiên để tìm đối thủ!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((r) => {
              const meta = STATUS_LABEL[r.status] || STATUS_LABEL.searching;
              return (
                <Link key={r.id} to={`/arena/${r.id}`} className="rounded-xl bg-[#0c0d14] border border-white/8 p-4 hover:border-amber-400/40 transition" data-testid={`arena-${r.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <img src={r.creator_avatar} alt="" className="w-10 h-10 rounded-full border border-white/10" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-display font-semibold truncate">{r.creator_nickname}</span>
                        {r.creator_verified && <ShieldCheck size={12} className="text-cyan-300" />}
                      </div>
                      <RankBadge elo={r.creator_elo} size="sm" showElo={false} />
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded border ${meta.bg} ${meta.color}`}>{meta.label}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/70 mb-2">
                    <span className="px-2 py-0.5 rounded bg-white/[0.05] border border-white/10">BO{r.best_of}</span>
                    <span className="px-2 py-0.5 rounded bg-white/[0.05] border border-white/10">{r.mode === "rank" ? "Rank" : "Giao hữu"}</span>
                    {r.table_number && <span className="px-2 py-0.5 rounded bg-white/[0.05] border border-white/10">Bàn {r.table_number}</span>}
                  </div>
                  {r.message && <div className="text-xs text-white/60 italic truncate mb-2">"{r.message}"</div>}
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1 text-amber-300 font-mono-num">
                      <Timer size={11} /> {formatDuration(r.started_search_at)}
                    </span>
                    <span className="flex items-center gap-1 text-white/50">
                      <Users size={11} /> {r.interested?.length || 0} quan tâm
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
};

const CreateModal = ({ onClose, onDone }) => {
  const [f, setF] = useState({ best_of: 1, mode: "rank", table_id: "", message: "" });
  const [tables, setTables] = useState([]);
  useEffect(() => { api.get("/tables").then(({ data }) => setTables(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/arena/requests", { ...f, best_of: Number(f.best_of), table_id: f.table_id || undefined });
      toast.success("Đã tạo kèo — hệ thống đã thông báo chat tổng");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-display font-semibold">Tạo kèo tìm đối thủ</h3><button onClick={onClose}><X size={16} /></button></div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-[10px] uppercase tracking-wider text-white/50">Best Of</span>
              <select value={f.best_of} onChange={(e) => setF({ ...f, best_of: e.target.value })} data-testid="arena-bo" className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
                <option value={1}>BO1</option><option value={3}>BO3</option><option value={5}>BO5</option>
              </select>
            </label>
            <label>
              <span className="text-[10px] uppercase tracking-wider text-white/50">Chế độ</span>
              <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="arena-mode" className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
                <option value="rank">Rank</option><option value="friendly">Giao hữu</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Bàn (tùy chọn)</span>
            <select value={f.table_id} onChange={(e) => setF({ ...f, table_id: e.target.value })} data-testid="arena-table" className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value="">-- Chọn sau --</option>
              {tables.map((t) => <option key={t.id} value={t.id}>Bàn {t.number}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Lời nhắn thương lượng</span>
            <input value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} maxLength={200} data-testid="arena-msg" placeholder="vd: Ai cùng trình Vàng vào thử?" className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-amber-400/60" />
          </label>
          <button type="submit" data-testid="arena-submit" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 text-black flex items-center justify-center gap-2">
            <Swords size={15} /> Bắt đầu tìm đối thủ
          </button>
        </form>
      </div>
    </div>
  );
};

const RequestRoom = ({ rid }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const load = () => api.get(`/arena/requests/${rid}`).then(({ data }) => setR(data));
  const loadChat = () => api.get(`/arena/requests/${rid}/chat`).then(({ data }) => setMsgs(data));

  useEffect(() => {
    load(); loadChat();
    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (ev.type === "arena.chat" && ev.msg?.arena_request_id === rid) setMsgs((m) => [...m, ev.msg].slice(-200));
          else if (["arena.interest", "arena.matched", "arena.cancelled"].includes(ev.type) && (ev.request?.id === rid || ev.request_id === rid)) load();
        } catch {}
      };
    } catch {}
    return () => { if (ws) ws.close(); };
  }, [rid]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault();
    const m = text.trim(); if (!m) return;
    await api.post(`/arena/requests/${rid}/chat`, { message: m });
    setText("");
  };

  const showInterest = async () => {
    try { await api.post(`/arena/requests/${rid}/interest`); toast.success("Đã gửi quan tâm — người tạo kèo sẽ thấy"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const confirmOpp = async (oppId) => {
    try {
      const { data } = await api.post(`/arena/requests/${rid}/confirm`, { opponent_id: oppId });
      toast.success(`Đã ghép trận! Trạng thái: ${data.status === "playing" ? "Đang đấu tại Bàn " + data.table_number : "Chờ admin gán bàn"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail); }
  };

  const cancel = async () => {
    if (!window.confirm("Hủy kèo?")) return;
    await api.post(`/arena/requests/${rid}/cancel`); load();
  };

  if (!r) return <div className="min-h-screen bg-[#05050a]"><TopNav /><div className="p-10 text-white/40">Đang tải...</div></div>;

  const isCreator = r.creator_id === user?.id;
  const alreadyInterested = r.interested?.some((i) => i.user_id === user?.id);
  const isOpen = r.status === "searching" || r.status === "negotiating";
  const meta = STATUS_LABEL[r.status] || STATUS_LABEL.searching;

  return (
    <div className="min-h-screen bg-[#05050a] flex flex-col">
      <TopNav />
      <div className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-6 flex flex-col">
        <Link to="/arena" className="inline-flex items-center gap-1 text-xs text-white/60 mb-3 hover:text-white"><ArrowLeft size={12} /> Quay lại danh sách</Link>

        {/* Header card */}
        <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-5 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <img src={r.creator_avatar} alt="" className="w-12 h-12 rounded-full border border-white/10" />
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="font-display font-semibold">{r.creator_nickname}</span>
                  {r.creator_verified && <ShieldCheck size={13} className="text-cyan-300" />}
                </div>
                <RankBadge elo={r.creator_elo} size="sm" />
              </div>
            </div>
            <span className={`text-xs px-3 py-1.5 rounded border ${meta.bg} ${meta.color} font-display font-semibold`}>{meta.label}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-white/[0.05] border border-white/10">BO{r.best_of}</span>
            <span className="px-2 py-1 rounded bg-white/[0.05] border border-white/10">{r.mode === "rank" ? "Rank" : "Giao hữu"}</span>
            {r.table_number && <span className="px-2 py-1 rounded bg-white/[0.05] border border-white/10">Bàn {r.table_number}</span>}
            <span className="px-2 py-1 rounded bg-amber-400/10 text-amber-300 border border-amber-400/30 font-mono-num flex items-center gap-1">
              <Timer size={11} /> {formatDuration(r.started_search_at)}
            </span>
          </div>
          {r.message && <div className="mt-3 text-sm text-white/80 italic">"{r.message}"</div>}
          {r.status === "matched" && r.created_match_id && (
            <div className="mt-3 p-2 rounded bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300">
              Đã tạo trận thành công. {r.table_number ? `TV đang hiển thị trận tại Bàn ${r.table_number}.` : "Đang chờ admin gán bàn."}
            </div>
          )}
        </div>

        {/* Action panel */}
        {isOpen && !isCreator && (
          <div className="rounded-xl bg-[#0c0d14] border border-white/8 p-4 mb-4 flex items-center gap-3">
            <div className="flex-1 text-sm text-white/70">
              {alreadyInterested ? "Bạn đã quan tâm. Chờ người tạo kèo chốt." : "Muốn tham gia? Gửi quan tâm để người tạo kèo chốt đối thủ."}
            </div>
            {!alreadyInterested && (
              <button onClick={showInterest} data-testid="show-interest" className="px-4 py-2 rounded-md text-sm font-medium bg-gradient-to-r from-amber-400 to-yellow-500 text-black">
                <Check size={14} className="inline mr-1" /> Tôi muốn đấu
              </button>
            )}
          </div>
        )}
        {isOpen && isCreator && (
          <div className="rounded-xl bg-[#0c0d14] border border-cyan-400/20 p-4 mb-4">
            <div className="font-display font-semibold text-sm mb-3 flex items-center gap-2"><Users size={14} className="text-cyan-300" /> Người quan tâm ({r.interested?.length || 0})</div>
            {(r.interested || []).length === 0 ? (
              <div className="text-xs text-white/40">Chưa có ai quan tâm. Đợi thêm chút...</div>
            ) : (
              <div className="space-y-2">
                {r.interested.map((i) => (
                  <div key={i.user_id} className="flex items-center gap-3 p-2 rounded bg-white/[0.02] border border-white/5">
                    <img src={i.avatar} alt="" className="w-8 h-8 rounded-full" />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5"><span className="text-sm">{i.nickname}</span>{i.verified && <ShieldCheck size={11} className="text-cyan-300" />}</div>
                      <div className="text-[10px] text-white/40">{i.rank} · {i.elo}</div>
                    </div>
                    <button onClick={() => confirmOpp(i.user_id)} data-testid={`confirm-opp-${i.user_id}`} className="px-3 py-1.5 rounded text-xs bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/25 flex items-center gap-1">
                      <Check size={11} /> Chấp nhận đấu
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={cancel} className="mt-3 text-xs text-red-400 hover:underline">Hủy kèo</button>
          </div>
        )}

        {/* Chat */}
        <div className="font-display font-semibold text-sm uppercase tracking-wider text-white/60 mb-2 flex items-center gap-2">
          <MessageCircle size={13} /> Chat thương lượng
        </div>
        <div className="flex-1 rounded-xl bg-[#0c0d14] border border-white/8 p-4 overflow-y-auto space-y-2 mb-3 min-h-[280px] max-h-[50vh]" data-testid="arena-chat-list">
          {msgs.length === 0 ? (
            <div className="text-white/40 text-center py-12 text-sm">Bắt đầu thương lượng (BO, bàn, thời gian gặp...)</div>
          ) : msgs.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
                <img src={m.sender_avatar} alt="" className="w-7 h-7 rounded-full shrink-0" />
                <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div className="text-[10px] text-white/50">{m.sender_nickname} · {m.sender_rank}</div>
                  <div className={`mt-0.5 px-3 py-1.5 rounded-2xl text-sm ${mine ? "bg-amber-400/15 border border-amber-400/30" : "bg-white/[0.04] border border-white/8"}`}>{m.message}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Thương lượng..." data-testid="arena-chat-input"
            className="flex-1 bg-[#0c0d14] border border-white/10 rounded-md px-4 py-3 text-sm outline-none focus:border-amber-400/60" />
          <button type="submit" className="px-5 rounded-md bg-gradient-to-r from-amber-400 to-yellow-500 text-black"><Send size={14} /></button>
        </form>
      </div>
    </div>
  );
};
